/**
 * Golden Hour: the wallet strip under the checkout button
 * (snippets/ghw-checkout-bar.liquid).
 *
 * Shopify decides per device which wallets appear: the shop has Shop Pay
 * and Google Pay on, a browser may show both, one, or none. The buttons
 * arrive later as child elements of <shopify-accelerated-checkout-cart>,
 * each with a height once drawn. This counts the ones that are actually
 * drawn and:
 *
 * - hides the whole strip, the line "or pay directly with" included, when
 *   none are, so there is no hole under the button (and shows it again if
 *   one arrives later);
 * - otherwise leaves it, with data-count for the one-or-more layout.
 *
 * Until Shopify has drawn anything, the strip stays as it is (label and
 * the Sandlight skeleton), so the pinned bar does not jump twice. If after
 * ten seconds still nothing is drawn, it counts as none.
 */
class GhwWallets extends HTMLElement {
  #resize = null;
  #mutate = null;
  /** The long wait after load. */
  #timer = 0;
  /** The short wait once Shopify has run and drawn nothing. */
  #settle = 0;
  /** What was last concluded: number of wallets drawn, or -1 not yet known. */
  #drawn = -1;
  #attrs = null;

  connectedCallback() {
    this.#resize = new ResizeObserver(() => this.#count(false));
    this.#mutate = new MutationObserver(() => this.#watch());
    this.#mutate.observe(this, { childList: true, subtree: true });
    // Horizon morphs the cart drawer after every cart change and puts the
    // attributes back as the server wrote them, which drops `hidden` and
    // `data-count`. The conclusion is kept here and written back at once,
    // so a device without wallets does not get the empty strip back.
    this.#attrs = new MutationObserver(() => this.#apply());
    this.#attrs.observe(this, { attributes: true, attributeFilter: ['hidden', 'data-count'] });
    this.#watch();
    this.#timer = window.setTimeout(() => this.#count(true), 10000);
  }

  disconnectedCallback() {
    this.#resize?.disconnect();
    this.#mutate?.disconnect();
    this.#attrs?.disconnect();
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#settle);
  }

  #host() {
    return this.querySelector('shopify-accelerated-checkout-cart');
  }

  #watch() {
    const host = this.#host();
    if (host) {
      this.#resize?.observe(host);
      for (const button of host.children) this.#resize?.observe(button);
    }
    this.#count(false);
  }

  /**
   * Shown with one wallet or more. Only real wallet buttons count: they
   * are Shopify's own elements (shop-pay-wallet-button,
   * shopify-google-pay-button and so on). The grey placeholder Shopify
   * draws first (div.wallet-button-wrapper) is not a wallet; it used to
   * count as one, so if the wallets never arrived the strip stayed up for
   * good, with "or pay directly with" above an empty grey bar (verified
   * 29 September 2026).
   *
   * While that placeholder is up she waits, up to the long timeout. With
   * neither placeholder nor buttons, Shopify has settled on none: the strip
   * goes after a short moment, so a redraw in between does not make the
   * bar jump.
   * @param {boolean} final - true when the long wait is over
   */
  #count(final) {
    const host = this.#host();
    const children = host ? Array.from(host.children) : [];
    const isButton = (el) => el.tagName.includes('-');
    const drawn = children.filter((el) => isButton(el) && el.getBoundingClientRect().height > 8).length;
    const placeholder = children.some((el) => !isButton(el));

    if (drawn > 0) {
      window.clearTimeout(this.#timer);
      window.clearTimeout(this.#settle);
      this.#settle = 0;
      this.#drawn = drawn;
      this.#apply();
      return;
    }
    if (final || !host) {
      this.#hide();
      return;
    }
    if (placeholder) return;
    // Nothing drawn and no placeholder. Before Shopify's element has run
    // there is nothing to judge; after that, give it a moment.
    if (host.matches(':defined') && !this.#settle) {
      this.#settle = window.setTimeout(() => {
        this.#settle = 0;
        this.#count(true);
      }, 1500);
    }
  }

  #hide() {
    this.#drawn = 0;
    this.#apply();
  }

  /** Write the conclusion onto the element, only where it differs. */
  #apply() {
    if (this.#drawn < 0) return;
    const hide = this.#drawn === 0;
    if (this.hidden !== hide) this.hidden = hide;
    const count = String(this.#drawn);
    if (this.dataset.count !== count) this.dataset.count = count;
  }
}

if (!customElements.get('ghw-wallets')) {
  customElements.define('ghw-wallets', GhwWallets);
}
